/* =============================================================
   useProcessConfig.js — Process configuration for a university
   -------------------------------------------------------------
   Manages the exchange process steps for the current university.
   Each university has its own editable copy of process steps
   (incoming and outgoing), initially applied from a standard template.

   Cache strategy (module-level Map):
     _stepsCache survives React component unmount/remount and
     Supabase token refreshes. On every mount the hook returns
     cached data immediately (no skeleton flash), then silently
     re-fetches in the background to stay fresh.

   Usage:
     const config = useProcessConfig('incoming');
     const config = useProcessConfig('outgoing');

   Returns:
     steps           — ordered array of process_steps rows for this
                       university + type, each with embedded document_type
     templates       — available global templates for this type
     docTypes        — full catalog of document types (for the dropdown)
     loading         — true ONLY on the very first fetch (no cached data yet)
     saving          — true while a write operation is in progress
     error           — error message string or null
     applyTemplate   — copies a template to this university (replaces steps)
     addStep         — appends a new step at the end
     updateStep      — updates label and/or document_type_id of a step
     deleteStep      — removes a step by id
     moveStep        — swaps a step up or down in the order
   ============================================================= */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase }  from '../lib/supabase';
import { useAuth }   from '../context/AuthContext';

const FETCH_TIMEOUT_MS = 8000;


/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
// Survives React unmount/remount and Supabase token refresh.
// Key: templateId (uuid)
const _stepsCache = new Map();

// Invalidate all cached steps for a given template
export function invalidateProcessCache(templateId) {
  if (templateId) _stepsCache.delete(templateId);
}


export function useProcessConfig(templateId) {
  const { user } = useAuth();

  // Cache key is the template UUID
  const cacheKey = templateId ?? null;
  const _cached  = cacheKey ? _stepsCache.get(cacheKey) : undefined;

  // Initialize from cache immediately — no skeleton flash on re-navigation
  const [steps,     setSteps]     = useState(_cached ?? []);
  const [templates, setTemplates] = useState([]);
  // Show loading spinner only if we have zero cached data
  const [loading,   setLoading]   = useState(_cached === undefined);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);

  const abortRef = useRef(null);


  /* ── fetchSteps ────────────────────────────────────────────── */
  // Always runs on mount, but only shows the spinner on the very
  // first fetch (stale-while-revalidate pattern).
  const fetchSteps = useCallback(async () => {
    if (!templateId || !cacheKey) { setLoading(false); return; }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    const timeoutId = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    // Show spinner only if we have no cached data
    if (!_stepsCache.has(cacheKey)) setLoading(true);

    try {
      const { data, error: fetchError } = await supabase
        .from('process_steps')
        .select('*, process_step_documents(id, document_type_id, deadline_date, item_type, document_types(id, name, icon, required_for, template_storage_path, template_file_name, type))')
        .eq('template_id', templateId)
        .order('step_order')
        .abortSignal(signal);

      if (signal.aborted) return;

      if (fetchError) {
        setError(fetchError.message);
      } else {
        const result = data ?? [];
        setSteps(result);
        _stepsCache.set(cacheKey, result); // keep cache fresh
      }
    } catch (err) {
      if (signal.aborted) return;
      setError(err?.message ?? 'Failed to load steps');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [templateId, cacheKey]); // eslint-disable-line


  /* ── initial load ──────────────────────────────────────────── */
  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  /* ── Abort on unmount ────────────────────────────────────────── */
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  /* ── Other templates for this university (for "copy from" feature) ── */
  // Shows the admin's OWN templates as copy sources — excludes the current one.
  useEffect(() => {
    if (!user?.universityId) return;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('university_process_templates')
          .select('id, name, type')
          .eq('university_id', user.universityId)
          .order('created_at');
        if (e) console.error('[useProcessConfig] templates:', e.message);
        // Exclude the current template so it doesn't appear as a copy source of itself
        setTemplates((data ?? []).filter(t => t.id !== templateId));
      } catch (err) {
        console.error('[useProcessConfig] unexpected:', err);
      }
    })();
  }, [user?.universityId, templateId]);


  /* ── applyTemplate ─────────────────────────────────────────── */
  // Copies all steps (+ their linked documents) from a SOURCE university
  // template into the CURRENT template (identified by the hook's templateId).
  // Does NOT call the old RPC — everything runs in JS.
  const applyTemplate = async (sourceTemplateId) => {
    if (!templateId || !sourceTemplateId || sourceTemplateId === templateId) return;
    setSaving(true);
    setError(null);

    try {
      // 1. Fetch steps + linked docs from the source template
      const { data: sourceSteps, error: fetchErr } = await supabase
        .from('process_steps')
        .select('label, step_order, process_step_documents(document_type_id, item_type, deadline_date)')
        .eq('template_id', sourceTemplateId)
        .order('step_order');

      if (fetchErr) { setError(fetchErr.message); setSaving(false); return; }
      if (!sourceSteps?.length) { setSaving(false); return; }

      // 2. Delete existing steps on the current template
      const { error: delErr } = await supabase
        .from('process_steps')
        .delete()
        .eq('template_id', templateId);

      if (delErr) { setError(delErr.message); setSaving(false); return; }

      // 3. Insert copied steps
      const { data: newSteps, error: insErr } = await supabase
        .from('process_steps')
        .insert(sourceSteps.map(s => ({
          template_id: templateId,
          label:       s.label,
          step_order:  s.step_order,
        })))
        .select('id, step_order');

      if (insErr) { setError(insErr.message); setSaving(false); return; }

      // 4. Insert linked documents for each new step
      // Match by step_order to pair old steps with their new IDs
      const junctionRows = [];
      for (const newStep of (newSteps ?? [])) {
        const source = sourceSteps.find(s => s.step_order === newStep.step_order);
        for (const psd of (source?.process_step_documents ?? [])) {
          junctionRows.push({
            step_id:          newStep.id,
            document_type_id: psd.document_type_id,
            item_type:        psd.item_type ?? 'document',
            deadline_date:    psd.deadline_date ?? null,
          });
        }
      }

      if (junctionRows.length > 0) {
        const { error: jErr } = await supabase
          .from('process_step_documents')
          .insert(junctionRows);
        if (jErr) { setError(jErr.message); setSaving(false); return; }
      }

      await fetchSteps(); // refresh UI
    } catch (err) {
      setError(err?.message ?? 'Copy failed');
    }

    setSaving(false);
  };


  /* ── addStep ───────────────────────────────────────────────── */
  const addStep = async (label) => {
    if (!templateId || !label.trim()) return;
    setSaving(true);
    setError(null);

    const maxOrder = steps.length > 0
      ? Math.max(...steps.map(s => s.step_order)) + 1
      : 0;

    const { error: insertError } = await supabase
      .from('process_steps')
      .insert({
        template_id: templateId,
        step_order:  maxOrder,
        label:       label.trim(),
      });

    if (insertError) setError(insertError.message);
    else             await fetchSteps();

    setSaving(false);
  };


  /* ── updateStep ────────────────────────────────────────────── */
  const updateStep = async (id, changes) => {
    setSaving(true);
    setError(null);

    const { error: updateError } = await supabase
      .from('process_steps')
      .update(changes)
      .eq('id', id);

    if (updateError) setError(updateError.message);
    else             await fetchSteps();

    setSaving(false);
  };


  /* ── deleteStep ────────────────────────────────────────────── */
  const deleteStep = async (id) => {
    setSaving(true);
    setError(null);

    const { error: deleteError } = await supabase
      .from('process_steps')
      .delete()
      .eq('id', id);

    if (deleteError) setError(deleteError.message);
    else             await fetchSteps();

    setSaving(false);
  };


  /* ── addTaskToStep ─────────────────────────────────────────── */
  // Links a task type to an existing step (same as addDocToStep but item_type='task').
  const addTaskToStep = async (stepId, taskTypeId) => {
    if (!taskTypeId) return;
    setSaving(true);
    setError(null);
    const { error: e } = await supabase
      .from('process_step_documents')
      .insert({ step_id: stepId, document_type_id: taskTypeId, item_type: 'task' });
    if (e) setError(e.message);
    else   await fetchSteps();
    setSaving(false);
  };


  /* ── addDocToStep ──────────────────────────────────────────── */
  // Links an extra document_type to an existing step via the junction table.
  const addDocToStep = async (stepId, documentTypeId) => {
    setSaving(true);
    setError(null);

    const { error: e } = await supabase
      .from('process_step_documents')
      .insert({ step_id: stepId, document_type_id: documentTypeId });

    if (e) setError(e.message);
    else   await fetchSteps();

    setSaving(false);
  };


  /* ── removeDocFromStep ─────────────────────────────────────── */
  // Removes a junction row by its own id (not the doc type id).
  const removeDocFromStep = async (junctionId) => {
    setSaving(true);
    setError(null);

    const { error: e } = await supabase
      .from('process_step_documents')
      .delete()
      .eq('id', junctionId);

    if (e) setError(e.message);
    else   await fetchSteps();

    setSaving(false);
  };


  /* ── updateDocDeadline ─────────────────────────────────────── */
  // Sets or clears the deadline_date on a single junction row.
  // Optimistic update applied immediately so the controlled date input
  // doesn't flicker back to empty while the Supabase round-trip runs.
  const updateDocDeadline = async (junctionId, date) => {
    // Apply immediately to local state — the input stays stable.
    setSteps(prev => prev.map(step => ({
      ...step,
      process_step_documents: (step.process_step_documents ?? []).map(psd =>
        psd.id === junctionId ? { ...psd, deadline_date: date || null } : psd
      ),
    })));

    setSaving(true);
    setError(null);

    const { error: e, count } = await supabase
      .from('process_step_documents')
      .update({ deadline_date: date || null }, { count: 'exact' })
      .eq('id', junctionId);

    if (e) {
      setError(e.message);
      await fetchSteps(); // revert optimistic update on error
    } else {
      await fetchSteps(); // confirm with fresh DB data
    }

    setSaving(false);
  };


  /* ── moveStep ──────────────────────────────────────────────── */
  const moveStep = async (id, direction) => {
    const idx     = steps.findIndex(s => s.id === id);
    if (idx === -1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= steps.length) return;

    setSaving(true);
    setError(null);

    const current = steps[idx];
    const swap    = steps[swapIdx];

    const [r1, r2] = await Promise.all([
      supabase.from('process_steps').update({ step_order: swap.step_order    }).eq('id', current.id),
      supabase.from('process_steps').update({ step_order: current.step_order }).eq('id', swap.id),
    ]);

    if (r1.error) setError(r1.error.message);
    else if (r2.error) setError(r2.error.message);
    else await fetchSteps();

    setSaving(false);
  };


  return {
    steps,
    templates,
    loading,
    saving,
    error,
    applyTemplate,
    addStep,
    updateStep,
    deleteStep,
    moveStep,
    addDocToStep,
    addTaskToStep,
    removeDocFromStep,
    updateDocDeadline,
  };
}
